import { useState } from 'react';
import { supabase } from '../lib/supabase';
import { useWaitlistStore } from '../lib/store';

// Email validation regex
const EMAIL_REGEX = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

// Common disposable email domains
const DISPOSABLE_DOMAINS = [
  'tempmail.com', 'throwawaymail.com', 'mailinator.com', 'guerrillamail.com',
  'yopmail.com', 'sharklasers.com', 'spam4.me', 'trashmail.com', 'temp-mail.org'
];

export function useWaitlist() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fetchCount = useWaitlistStore(state => state.fetchCount);

  const validateEmail = (email: string): { isValid: boolean; error?: string } => {
    // Trim and convert to lowercase
    email = email.trim().toLowerCase();

    // Basic format check
    if (!EMAIL_REGEX.test(email)) {
      return { isValid: false, error: 'Ogiltig e-postadress' };
    }

    // Length checks
    if (email.length < 5) {
      return { isValid: false, error: 'E-postadressen är för kort' };
    }
    if (email.length > 255) {
      return { isValid: false, error: 'E-postadressen är för lång' };
    }

    // Check for consecutive dots
    if (email.includes('..')) {
      return { isValid: false, error: 'Ogiltig e-postadress' };
    }

    // Extract domain
    const domain = email.split('@')[1];

    // Check for disposable email domains
    if (DISPOSABLE_DOMAINS.includes(domain)) {
      return { isValid: false, error: 'Temporära e-postadresser är inte tillåtna' };
    }

    // Check domain format
    if (!/^[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,}$/.test(domain)) {
      return { isValid: false, error: 'Ogiltig e-postdomän' };
    }

    return { isValid: true };
  };

  const submitEmail = async (email: string) => {
    try {
      setIsSubmitting(true);
      setError(null);

      // Validate email
      const validation = validateEmail(email);
      if (!validation.isValid) {
        setError(validation.error || 'Ogiltig e-postadress');
        return false;
      }

      const { error: insertError } = await supabase
        .from('waitlist')
        .insert([{ email: email.trim().toLowerCase() }]);

      if (insertError) {
        if (insertError.code === '23505') {
          setError('Du är redan registrerad på väntelistan');
          return false;
        }
        if (insertError.code === '23514') { // Check constraint violation
          setError('Ogiltig e-postadress');
          return false;
        }
        throw new Error('Ett fel uppstod. Försök igen senare.');
      }

      // Fetch updated count after successful submission
      await fetchCount();
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ett fel uppstod. Försök igen senare.');
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  return { submitEmail, isSubmitting, error };
}